import { isNil } from 'lodash';
import { ReactNode, useContext, useMemo } from 'react';
import { applyComponentOverrides } from '@shared/scenes/entity/util/useViewComponents';
import { useViewTemplate, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { WorkflowComponentsContext } from './WorkflowComponentsContext';

interface Props {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  /**
   * Component set to provide instead of the workflow template's saved one. Used by the builder
   * preview, whose components are unsaved drafts. Must already have overrides applied.
   */
  components?: API.AnyComponent[];
  children: ReactNode;
}

/**
 * Resolves the workflow's full component set for the form beneath it. Both templates are already
 * fetched by the form's own data hooks, so these queries are Apollo cache reads rather than
 * requests. See {@link WorkflowComponentsContext}.
 */
export function WorkflowComponentsProvider({
  workflowTemplateId,
  viewTemplateId,
  components,
  children,
}: Props) {
  // An enclosing provider has already resolved the set — the builder preview provides its drafts
  // that way — so inherit it rather than looking the saved one up again.
  const inherited = useContext(WorkflowComponentsContext);
  const resolved = components ?? inherited;

  // A resolved set means nothing needs fetching — nil ids skip both queries.
  const templateId = isNil(resolved) ? workflowTemplateId : null;
  const { workflowTemplate } = useWorkflowTemplate(templateId);
  const { viewTemplate } = useViewTemplate(templateId, viewTemplateId);

  const value = useMemo(
    () => resolved ?? applyComponentOverrides(workflowTemplate?.components ?? [], viewTemplate),
    [resolved, workflowTemplate?.components, viewTemplate],
  );

  return (
    <WorkflowComponentsContext.Provider value={value}>
      {children}
    </WorkflowComponentsContext.Provider>
  );
}
